import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import hubLayout from 'styles/css/hubLayout.module.css';
import { Events } from 'type/type';
import { getRecorderDdnsEvent } from 'api/api';
import Loading from 'components/common/Loading';
import { useRecoilState } from 'recoil';
import { isDarkAtom } from 'styles/atoms';
import { eventLabels, formatYMToString } from 'pages/utils/commonUtils';
import { CommonTable, CommonTableWrap, FilterContentContainer } from 'pages/Layout';
import { PrimaryButton } from 'components/common/Button';
import Pagenation from 'components/common/Pagenation';

interface SearchInputType {
  mac: string,
  type: string,
  event_date: string,
};

function RecorderEvent() {
  const { recorderMac } = useParams();
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [darkAtom, setDarkAtom] = useRecoilState(isDarkAtom);

  // -------- 페이지네이션
  const buttonPerPage = 10; // 보여줄 페이지네이션 버튼 수
  const [page, setPage] = useState(1); // 현재 페이지
  const [limit] = useState(10); // 보여줄 게시물 개수
  const [totalCount, setTotalCount] = useState(0);
  const numPages = Math.ceil(totalCount / limit); // 총 페이지 수
  const [startButtonNum, setStartButtonNum] = useState(1); // 버튼 시작 숫자
  
  // 페이지네이션 버튼 클릭 시
  const isClickButton = (str:string) => {
      if (str === 'prev') handlePrevPage();
      else handleNextPage();
  };

  // 이전페이지 이동(buttonPerPage만큼 -)
  const handlePrevPage = () => {
      const prevPage = Math.max(startButtonNum - buttonPerPage, 1);
      setStartButtonNum(prevPage);
      setPage(prevPage);
  };

  // 다음페이지 이동(buttonPerPage만큼 +)
  const handleNextPage = () => {
      const nextPage = Math.min(startButtonNum + buttonPerPage, numPages);
      setStartButtonNum(nextPage);
      setPage(nextPage);
  };

  // ----------- A/S 목록
  // 필터obj
  const [searchObj, setSearchObj] = useState<SearchInputType>({
    mac: recorderMac || "X", type: "", event_date: formatYMToString(new Date())
  });
  // A/S 리스트
  const [eventList, setEventList] = useState<Events[]>([]);

  const searchHandle = () => {
    if (page === 1) {
      getEventList(page, limit);
    } else {
      setStartButtonNum(1);
      setPage(1);
    }
  }

  // A/S 목록 조회
  const getEventList = useCallback(async (page: number, limit: number) => {
    setLoading(true);
    const { mac, type, event_date } = searchObj;
  
    // state가 undefined이면 객체에서 제외
    const param = {mac, type, event_date};
    
    const eventRes = await getRecorderDdnsEvent(page, limit, param);
    if (eventRes.code === 200) {
      setEventList(eventRes.data);
      setTotalCount(eventRes.total_count);
    }
    setLoading(false);
  }, [searchObj]);

  // 검색 type 변경
  const handleSearchType = (type: string) => {
    setSearchObj(prev => ({...prev, type }));
  };
  // 검색 날짜 변경
  const handleSearchDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
  
    // 유효한 날짜로 상태 업데이트
    setSearchObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    getEventList(page, limit);
  }, [page, limit]);
  // Dark mode 상태 설정
  useEffect(() => {
    const isDark = searchParams.get("isDark");
    setDarkAtom(isDark === 'true');
  }, [searchParams, setDarkAtom]);
  return (
    <>
        <div className={`${hubLayout.acc__cnt}`} style={{ padding: "0 20px", display: "flex", flexDirection: "column", gap: "2rem" }}>
          <div className={`${hubLayout.sec_title}`}>
            <h2 style={{ color: darkAtom ? '#fff' : '' }}>{recorderMac} Event 조회</h2>
          </div>
          <FilterContentContainer>
            <div className='flex-row'>
              <label>타입</label>
              <div>
                <select name="type" onChange={(e) => handleSearchType(e.target.value)} value={searchObj.type} >
                  <option value="">전체</option>
                  {Object.entries(eventLabels).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className='flex-row'>
              <label>등록일</label>
              <div className='flex-row'>
                <input type='month' name='event_date' value={searchObj.event_date} onChange={handleSearchDate} />
              </div>
            </div>
            <PrimaryButton type="button" onClick={searchHandle}>조회</PrimaryButton>
            <div>
              검색 결과: <span style={{ fontWeight: "600" }}>{totalCount}</span>건
            </div>
          </FilterContentContainer>
          {loading ? <Loading />
          :
            <>
              <CommonTableWrap>
                <CommonTable>
                  <thead>
                    <tr>
                      <th className='w-5'>No</th>
                      <th className='w-10'>타입</th>
                      <th className='w-10'>관제여부</th>
                      <th className='w-10'>PUSH 전송 여부</th>
                      <th className='w-10'>채널</th>
                      <th className='w-20'>발생일자</th>
                      <th className='w-10'>처리자</th>
                      <th className='w-10'>처리ID</th>
                      <th className='w-15'>처리시간</th>
                      <th className='w-10'>처리타입</th>
                      <th className='w-20'>처리일자</th>
                    </tr>
                  </thead>
                  <tbody>
                    {totalCount === 0 ?
                      <tr><td colSpan={11}>검색결과가 없습니다.</td></tr>
                      :
                      eventList.map((event, idx) => 
                        <tr key={`event_${idx}`} >
                          <td>{(page - 1) * 10 + idx + 1}</td>
                          <td className='ellipsis-text'>{event.eType}</td>
                          <td className='ellipsis-text'>{event.monitor}</td>
                          <td className='ellipsis-text'>{event.push}</td>
                          <td className='ellipsis-text'>{event.eCh}</td>
                          <td>{event.eDate}</td>
                          <td>{event.processorName}</td>
                          <td>{event.processorId}</td>
                          <td>{event.processTime}</td>
                          <td>{event.processType}</td>
                          <td>{event.processDetail}</td>
                        </tr>
                      )
                    }
                  </tbody>
                </CommonTable>
              </CommonTableWrap>
              <Pagenation
                totalCount={totalCount}
                total={numPages}
                limit={limit}
                page={page}
                setPage={setPage}
                startButtonNum={startButtonNum}
                setClickButton={isClickButton}
              />
            </>
          }
        </div>
    </>
  );
}

export default RecorderEvent;